import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminCosts } from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

export default function AdminCosts() {
  const [days, setDays] = useState(7);
  const q = useQuery({
    queryKey: ["admin-costs", days],
    queryFn: () => getAdminCosts(days),
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <div className="py-6 text-center text-taupe">Loading…</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">Failed to load.</div>;

  const d = q.data;
  const today = d.today;
  const maxUsd = d.period_timeseries.reduce((m, r) => Math.max(m, r.usd), 0.0001);

  return (
    <div className="space-y-5">
      {/* Period selector + headline tiles */}
      <Card className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <SectionLabel>Period</SectionLabel>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
          >
            <option value={1}>Today only</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <Tile
          label="Today total"
          primary={`$${today.total_usd.toFixed(4)}`}
          secondary={`${today.calls} calls`}
        />
        <Tile
          label="Web searches today"
          primary={`${today.searches}`}
          secondary={`$${today.search_usd.toFixed(4)} surcharge`}
        />
        <Tile
          label="Cache hit rate"
          primary={`${(today.cache_hit_rate * 100).toFixed(1)}%`}
          secondary={`${(today.cache_read_tokens / 1000).toFixed(1)}k tokens reused`}
        />
        <Tile
          label="Search cache savings"
          primary={`$${d.search_cache.estimated_savings_usd.toFixed(4)}`}
          secondary={`${d.search_cache.total_hits} hits / ${d.search_cache.rows} rows`}
        />
      </Card>

      {/* By model breakdown */}
      <Card>
        <SectionLabel>Today by model</SectionLabel>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-cream-border text-left text-taupe">
                <th className="py-2 pr-4 font-medium">Model</th>
                <th className="py-2 pr-4 text-right font-medium">USD</th>
                <th className="py-2 text-right font-medium">Calls</th>
              </tr>
            </thead>
            <tbody>
              {today.by_model.map((m) => (
                <tr key={m.model} className="border-b border-cream-border/60">
                  <td className="py-2 pr-4 font-mono text-[12px]">{m.model}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">${m.usd.toFixed(4)}</td>
                  <td className="py-2 text-right tabular-nums">{m.calls}</td>
                </tr>
              ))}
              {today.by_model.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-3 text-center text-taupe">
                    No calls today yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Timeseries bar chart */}
      <Card>
        <SectionLabel>Daily spend (last {d.period_days} days)</SectionLabel>
        <div className="mt-3 flex h-40 items-end gap-1">
          {d.period_timeseries.map((r) => (
            <div
              key={r.date}
              className="flex flex-1 flex-col items-center gap-1"
              title={`${r.date} · $${r.usd.toFixed(4)} · ${r.calls} calls · ${r.searches} searches`}
            >
              <div className="w-full rounded-t bg-amber/70" style={{ height: `${(r.usd / maxUsd) * 100}%` }} />
              <span className="text-[9px] text-taupe">{r.date.slice(5)}</span>
            </div>
          ))}
          {d.period_timeseries.length === 0 ? (
            <div className="flex w-full items-center justify-center text-taupe">
              No data in this window
            </div>
          ) : null}
        </div>
      </Card>

      {/* Search cache top entries */}
      <Card>
        <SectionLabel>Top cached searches</SectionLabel>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-cream-border text-left text-taupe">
                <th className="py-2 pr-4 font-medium">Query</th>
                <th className="py-2 pr-4 font-medium">Topic</th>
                <th className="py-2 pr-4 text-right font-medium">Hits</th>
                <th className="py-2 pr-4 text-right font-medium">Results</th>
                <th className="py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.search_cache.top.map((r) => (
                <tr key={r.id} className="border-b border-cream-border/60">
                  <td className="py-2 pr-4 max-w-md truncate" title={r.query}>
                    {r.query}
                  </td>
                  <td className="py-2 pr-4 text-taupe">{r.topic ?? "—"}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.hits}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.result_count}</td>
                  <td className="py-2">
                    {r.fresh ? (
                      <span className="rounded-full bg-amber/30 px-2 py-0.5 text-[11px] text-amber-dark">
                        active
                      </span>
                    ) : (
                      <span className="rounded-full bg-cream-border px-2 py-0.5 text-[11px] text-taupe">
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {d.search_cache.top.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-3 text-center text-taupe">
                    No cached searches yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Tile({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <div className="min-w-[140px]">
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1 font-serif text-[22px] font-medium text-navy-deep tabular-nums">
        {primary}
      </div>
      {secondary ? <div className="text-[11px] text-taupe">{secondary}</div> : null}
    </div>
  );
}
